import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { rpcClient } from "@/lib/rpc";

/**
 * Health-check tile for the calibration prompts. Surfaces every
 * `scoring:dim:<dim>` row the calibration agent reads so an operator can
 * self-detect the production "score=1.00 across the board" failure mode
 * — that bug fired silently when a row was missing or the `data` column
 * was malformed. The runtime defenses (loud logger + missing-rubric
 * banner in the live panel) catch new occurrences, but the tile lets
 * someone notice the issue before they trigger a calibration.
 *
 * Mounts under `IV · engenharia`.
 */

interface PromptHealthRow {
  section: string;
  dim: string;
  present: boolean;
  okShape: boolean;
  bytes: number;
  /** Null when the row is fully missing from the DB. */
  updatedAt: string | null;
}

interface PromptHealthResponse {
  rows: PromptHealthRow[];
}

// biome-ignore lint/security/noSecrets: query key, not a secret
const QK = ["labs", "calibrationRunsPromptHealth"];

function formatRel(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days <= 0) {
    const hours = Math.floor(diff / 3_600_000);
    if (hours <= 0) return "agora há pouco";
    if (hours === 1) return "há 1 hora";
    return `há ${hours} horas`;
  }
  if (days === 1) return "ontem";
  if (days < 30) return `há ${days} dias`;
  const months = Math.floor(days / 30);
  return months === 1 ? "há 1 mês" : `há ${months} meses`;
}

export function PromptHealthSection() {
  const { data, isLoading, isError } = useQuery<PromptHealthResponse>({
    queryKey: QK,
    queryFn: () => rpcClient.labs.calibrationRunsPromptHealth(),
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Carregando saúde dos prompts…
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="py-6 text-sm text-muted-foreground">
        Não consegui carregar a saúde dos prompts. Tente recarregar.
      </div>
    );
  }

  const broken = data.rows.filter((r) => !r.okShape);
  const total = data.rows.length;

  return (
    <div className="prompt-health py-3 space-y-3" data-testid="prompt-health-section">
      {/* The accordion subtitle (in IV · engenharia) already explains what this
          tile does. Repeating the same blurb here was wasted vertical space —
          the summary pill below now leads. */}
      <div
        className={`prompt-health-summary ${broken.length === 0 ? "is-ok" : "is-bad"}`}
        data-testid="prompt-health-summary"
      >
        {broken.length === 0 ? (
          <CheckCircle2 className="h-4 w-4" aria-hidden />
        ) : (
          <AlertTriangle className="h-4 w-4" aria-hidden />
        )}
        <span>
          <strong className="tabular-nums">{total - broken.length}</strong> de{" "}
          <strong className="tabular-nums">{total}</strong> rubricas saudáveis
          {broken.length > 0 && (
            <>
              {" "}
              ·{" "}
              <strong className="text-rose-700">{broken.length} ausente(s) ou malformada(s)</strong>
            </>
          )}
        </span>
      </div>
      <ul className="prompt-health-list">
        {data.rows
          .slice()
          .sort((a, b) => Number(a.okShape) - Number(b.okShape) || a.dim.localeCompare(b.dim))
          .map((r) => (
            <li
              key={r.section}
              className={`prompt-health-row ${r.okShape ? "is-ok" : "is-bad"}`}
              data-testid={`prompt-health-row-${r.dim}`}
            >
              <span className="prompt-health-row-name">
                <code>{r.section}</code>
              </span>
              <span className="prompt-health-row-bytes tabular-nums">
                {r.present ? `${r.bytes.toLocaleString("pt-BR")} bytes` : "—"}
              </span>
              <span className="prompt-health-row-when text-muted-foreground tabular-nums">
                {r.updatedAt ? formatRel(r.updatedAt) : "(ausente)"}
              </span>
              <span className={`prompt-health-row-pill ${r.okShape ? "is-ok" : "is-bad"}`}>
                {r.okShape ? "OK" : "Rubrica ausente — reseed"}
              </span>
            </li>
          ))}
      </ul>
      {broken.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Para reseed: vá em <em>IV · engenharia → Sincronizar com o disco</em> e clique em
          "Recarregar rubricas e prompts". Os arquivos vêm de{" "}
          <code>docs/labs/prompts/scoring/dim-*.md</code>.
        </p>
      )}
    </div>
  );
}
